/**
 * Body for the "Revisual default" option. Nothing renders when default is
 * already the live domain — there's nothing to switch. When a subdomain or
 * custom domain is connected (or a custom domain is mid-verification), the
 * body is a warning alert stating the disconnect + consequence, followed by
 * the "Switch to default" action — warnings always precede the action they
 * qualify.
 */
import React, { useCallback } from "react";
import appConfig from "../../../../../appConfig";
import Button from "../../../theme/untitled/Button/Button";
import Icon from "../../../theme/untitled/icons/Icon";
import FieldError from "./FieldError";
import { parseGeneralError } from "./errorHelpers";
import styles from "./DefaultDomainOption.module.scss";

const DefaultDomainOption = ({
  domainType,
  isVerificationPending,
  pendingDomain,
  onRequestRevert,
  isLoading,
  error,
}) => {
  const isReverting = domainType !== "default" || isVerificationPending;
  // While a custom domain is only pending, domain_type is still "default" —
  // nothing about the live address changes, only the in-progress setup is
  // cancelled. Distinct copy from an actual live-domain switch below.
  const isCancelOnly = domainType === "default" && isVerificationPending;

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onRequestRevert();
    },
    [onRequestRevert]
  );

  if (!isReverting) return null;

  const errorMessage = error
    ? parseGeneralError(error, "The service is unavailable. Try again in a moment.")
    : null;

  return (
    <>
      <div className={styles.warning}>
        <Icon name="AlertTriangle" size={20} className={styles.warningIcon} />
        <p className={styles.warningText}>
          {isCancelOnly ? (
            <>
              Cancels your setup for <strong>{pendingDomain}</strong> and removes its DNS
              records. Links stay on <strong>{appConfig.defaultPublicDomain}</strong>.
            </>
          ) : (
            <>
              Switching to the default domain will break any existing QR codes and shared links
              across all your widgets. Your links will move to{" "}
              <strong>{appConfig.defaultPublicDomain}</strong>.
            </>
          )}
        </p>
      </div>
      <Button hierarchy="primary" onClick={onSubmit} disabled={isLoading} loading={isLoading}>
        {isLoading ? "Saving…" : isCancelOnly ? "Cancel setup" : "Switch to default"}
      </Button>
      <FieldError message={errorMessage} />
    </>
  );
};

export default DefaultDomainOption;
